<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import { vEnter } from '@/lib/enterKey'
import AddonIssueModal from '@/components/AddonIssueModal.vue'
import CustomerProfileCard from '@/components/CustomerProfileCard.vue'
import FilterClearButton from '@/components/FilterClearButton.vue'
import ModulePicker from '@/components/ModulePicker.vue'
import PolicyEditor from '@/components/PolicyEditor.vue'
import TermField from '@/components/TermField.vue'
import QuotaEditor from '@/components/QuotaEditor.vue'
import QuotaSummary from '@/components/QuotaSummary.vue'
import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type {
  Customer,
  CustomerDetail,
  CustomerProfile,
  CustomerUser,
  License,
  LicenseAddon,
} from '@/api/ls'
import { canActivateDirectly, policyFor, policyHint } from '@/lib/activation'
import { LICENSE_FILENAME, saveTextFile } from '@/lib/download'
import { effectiveStatus, statusLabel } from '@/lib/modules'
import { usePersistedFilters } from '@/lib/persistedFilters'
import {
  activationPolicyLabel,
  defaultModulesOf,
  defaultTermTotal,
  isInternalPlan,
  optionOf,
  issuablePlans,
  termOptionsWith,
  termLabel,
} from '@/lib/products'
import { knownErrorText } from '@/lib/apiErrors'
import { quotaAddonHint } from '@/lib/licenseLabels'
import { quotaLabel } from '@/lib/quotas'
import { seedQuotas } from '@/lib/seedQuotas'
import { MINUTES_PER_DAY, formatRemaining, formatTerm, splitMinutes, totalMinutes } from '@/lib/terms'
import { ROLE_LABELS } from '@/lib/roles'
import { fmtSeen, staleLevel } from '@/lib/staleness'
import { useAuthStore } from '@/stores/auth'
import { useDataStore } from '@/stores/data'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const store = useDataStore()
const dialogs = useDialogsStore()
const auth = useAuthStore()

// 清單與過濾（下拉送伺服器，桶留前端——同 `listLicenses` 的原則）。
// ⚠️ `store.customers` 是**全站共用**的快取（別的頁靠它把 customer_id 翻成名字），
//    所以過濾結果放在本頁自己的 `rows`，不要拿過濾後的清單去蓋掉 store。
// 批186：四個條件記住。
// - `resellerFilter`（批93-C）：''＝全部、`none`＝只看直營、uuid＝該代理商的客戶。
// - `emptyOnly`（批93-C）：空組織（無授權、無申請、基本資料未填）——不自動歸戶之後長出來的殘渣。
// - `sourceFilter`（批141 裁示 25）：`platform`＝合作平台的組織（含已解除關聯的）、`direct`＝其餘；伺服器端條件。
const pf = usePersistedFilters(
  'customers',
  { kindFilter: '', resellerFilter: '', emptyOnly: false, sourceFilter: 'all' as ls.CustomerSource },
  {
    oneOf: {
      kindFilter: ['', 'company', 'personal', 'unset'],
      sourceFilter: ['all', 'direct', 'platform'],
    },
  },
)
const { kindFilter, resellerFilter, emptyOnly, sourceFilter } = pf.refs
const { dirty: filtersDirty } = pf
/** 下拉是 `@change` 驅動，程式改值不會觸發，所以清除後要自己重載。 */
const clearFilters = () => {
  pf.clear()
  void reloadList()
}
const rows = ref<Customer[]>([])
/**
 * keyset 續頁游標（批93-D LS-4）。**兩條線**：
 * - 沒過濾時 `rows` 就是 store 的共用快取 → 游標跟著 `store.customersNextCursor` 走，
 *   續頁交給 `store.loadMoreCustomers()`（別的頁的下拉才會一起變完整）。
 * - 有過濾時 `rows` 是本頁自己打的一份 → 游標與續頁都留在本頁（不可以拿過濾後的清單
 *   去蓋掉 store，見上面那條註解）。
 */
const nextCursor = ref<string | null>(null)
const loadingMore = ref(false)

const listFiltered = computed(
  () =>
    !!kindFilter.value || !!resellerFilter.value || emptyOnly.value || sourceFilter.value !== 'all',
)

function filterOpts() {
  return {
    kind: kindFilter.value || undefined,
    resellerId: resellerFilter.value || undefined,
    empty: emptyOnly.value || undefined,
    source: sourceFilter.value,
  }
}

/**
 * 批141（裁示 24／25）：平台客戶——**含已解除關聯的**——不可刪除、不可合併、不給 portal
 * （不能邀請成員）。後端一律 409 `platform_managed_customer`；這裡把按鈕停用並講原因。
 */
function platformNote(c: Customer | null | undefined): string | null {
  const p = c?.platform
  if (!p) return null
  return p.detached_at
    ? `這是合作平台「${p.name}」的組織（${p.external_ref}，已解除關聯）——不可刪除或合併，也不使用客戶 portal。`
    : `這是合作平台「${p.name}」的組織（${p.external_ref}）——授權由平台代辦，不可刪除或合併，也不使用客戶 portal。`
}
const selectedPlatformNote = computed(() => platformNote(selected.value))

async function reloadList() {
  await store.loadCustomers()
  if (listFiltered.value) {
    const page = await ls.listCustomers(filterOpts())
    rows.value = page.items
    nextCursor.value = page.next_cursor
  } else {
    rows.value = store.customers
    nextCursor.value = store.customersNextCursor
  }
}

/** 續載下一頁（append）。沒有游標＝已經載完，直接回。 */
async function loadMore() {
  if (!nextCursor.value || loadingMore.value) return
  loadingMore.value = true
  try {
    if (listFiltered.value) {
      const page = await ls.listCustomers({ ...filterOpts(), cursor: nextCursor.value })
      rows.value = [...rows.value, ...page.items]
      nextCursor.value = page.next_cursor
    } else {
      await store.loadMoreCustomers()
      // ★ store 是整個換掉陣列（`customers.value = [...]`），所以這裡要**重新取值**
      //   ——沿用舊的參照會停在上一頁。
      rows.value = store.customers
      nextCursor.value = store.customersNextCursor
    }
  } catch (e) {
    await fail(e, '讀取下一頁失敗')
  } finally {
    loadingMore.value = false
  }
}

onMounted(() => {
  void reloadList()
  // 核發框的產品下拉／模組與配額編輯器都靠憑證設計；懶載一次（admin+ 才進得來本頁）。
  void store.ensureProducts().catch(() => {})
  // 代理商欄／篩選／指派下拉；admin 讀不到（superadmin 端點）就退化成用列上帶的名字。
  void store.ensureResellers()
})

/** 代理商篩選／指派下拉的選項；store 讀不到就從已載入的客戶列湊出來。 */
const resellerOptions = computed<{ id: string; name: string }[]>(() => {
  if (store.resellers.length) {
    return store.resellers.filter((r) => r.active).map((r) => ({ id: r.id, name: r.name }))
  }
  const seen = new Map<string, string>()
  for (const c of store.customers) {
    if (c.reseller_id) seen.set(c.reseller_id, c.reseller_name ?? c.reseller_id.slice(0, 8))
  }
  return [...seen].map(([id, name]) => ({ id, name }))
})
/** 客戶列的代理商顯示名；未掛＝直營。 */
function resellerOf(c: Customer | null | undefined): string {
  if (!c?.reseller_id) return '直營'
  return c.reseller_name ?? store.resellerName(c.reseller_id)
}

async function fail(e: unknown, fallback: string) {
  const detail = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  // 批155：跨畫面共用的錯誤碼（內部套裝／模組…）先查中文對照。
  const known = knownErrorText(detail)
  const msg = known ?? (detail == null ? fallback : typeof detail === 'string' ? detail : JSON.stringify(detail))
  await dialogs.notify({ title: '操作失敗', message: msg })
}
function fmt(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '—'
}

// --- 建立客戶 ---
const newName = ref('')
const newDomain = ref('')
async function createCustomer() {
  if (!newName.value.trim()) return
  try {
    await ls.createCustomer(newName.value.trim(), newDomain.value.trim() || undefined)
    newName.value = ''
    newDomain.value = ''
    await reloadList()
  } catch (e) {
    await fail(e, '建立客戶失敗')
  }
}

// --- 選取客戶 + 明細 ---
const selected = ref<Customer | null>(null)
const portalUsers = ref<CustomerUser[]>([])
const newUserEmail = ref('')
const hosts = ref<ls.Host[]>([])
const offlineLics = ref<License[]>([])

// --- 客戶名稱／網域就地編輯（PUT 部分更新；網域撞到別的客戶後端回 409）---
const custEditing = ref(false)
const custName = ref('')
const custDomain = ref('')
function startEditCustomer() {
  if (!selected.value) return
  custName.value = selected.value.name
  custDomain.value = selected.value.domain ?? ''
  custEditing.value = true
}
async function saveCustomer() {
  if (!selected.value) return
  const name = custName.value.trim()
  if (!name) return
  try {
    const updated = await ls.updateCustomer(selected.value.id, { name, domain: custDomain.value.trim() || null })
    custEditing.value = false
    await reloadList() // 左清單同步
    selected.value = rows.value.find((c) => c.id === updated.id) ?? updated
  } catch (e) {
    await fail(e, '更新客戶失敗')
  }
}

// --- 刪除客戶（僅 superadmin；對話框列出會一併消失的東西、輸入客戶名稱才能刪）---
const delOpen = ref(false)
const delPreview = ref<ls.CustomerDeletionPreview | null>(null)
const delName = ref('')
const delBusy = ref(false)
async function openDelete() {
  if (!selected.value) return
  delName.value = ''
  try {
    delPreview.value = await ls.customerDeletionPreview(selected.value.id)
    delOpen.value = true
  } catch (e) {
    await fail(e, '讀取刪除資訊失敗')
  }
}
async function doDelete() {
  if (!selected.value || !delPreview.value?.deletable) return
  if (delName.value.trim() !== selected.value.name) return
  delBusy.value = true
  try {
    await ls.deleteCustomer(selected.value.id)
    const name = selected.value.name
    delOpen.value = false
    selected.value = null
    await Promise.all([reloadList(), store.loadLicenses()])
    await dialogs.notify({
      title: '已刪除',
      message: `客戶「${name}」與其授權歷史、申請、portal 帳號、主機連結已全部刪除。`,
    })
  } catch (e) {
    await fail(e, '刪除失敗')
  } finally {
    delBusy.value = false
  }
}

// --- 歸屬代理商（批93-C；admin+）--------------------------------------------
//
// 替換語意，不是加掛：一個客戶只能屬於一個代理商或不屬於任何一個（單一 nullable FK）。
// ★ 改派**不會**動既有授權／申請上的 `reseller_id` 快照——那是「當時是誰經手的」，
//   改掉會讓歷史對不起來。只影響之後的可見性與新申請歸屬（續約則以當下歸屬為準）。
const assignOpen = ref(false)
const assignTarget = ref('')
const assignBusy = ref(false)

function startAssign() {
  assignTarget.value = selected.value?.reseller_id ?? ''
  assignOpen.value = true
}
async function saveAssign() {
  if (!selected.value) return
  assignBusy.value = true
  try {
    const updated = await ls.setCustomerReseller(selected.value.id, assignTarget.value || null)
    assignOpen.value = false
    await reloadList()
    selected.value = rows.value.find((c) => c.id === updated.id) ?? updated
  } catch (e) {
    await fail(e, '指派代理商失敗')
  } finally {
    assignBusy.value = false
  }
}

// --- 合併到其他客戶（批93-C §2.7；superadmin）--------------------------------
//
// A（目前選取的）併入 B。為什麼不是改個 customer_id 就好：已簽出的授權 payload 內嵌
// `customer_id`／`customer_name`，子憑證的綁定鍵也含 `customer_id` → 只改 DB 欄會讓
// B 名下新加購的子證與 A 時期簽的主證對不起來，產品端直接拒收。所以合併＝**把 A 的
// active 主證與子證同內容重簽到 B**，線上站靠心跳自動換證，airgapped 站要人工送檔。
const mergeOpen = ref(false)
const mergeQuery = ref('')
const mergeTargetId = ref('')
const mergePreview = ref<ls.MergePreview | null>(null)
const mergePreviewBusy = ref(false)
const mergeConfirmName = ref('')
const mergeForceReseller = ref(false)
const mergeBusy = ref(false)
const mergeResult = ref<ls.MergeResult | null>(null)

/** B 的候選（排除 A 自己）；輸入關鍵字後才收斂，避免一次畫幾百列。 */
const mergeCandidates = computed(() => {
  const q = mergeQuery.value.trim().toLowerCase()
  return store.customers
    .filter((c) => c.id !== selected.value?.id)
    // 批141：合併的另一方也不可以是平台客戶（後端 409 `platform_managed_customer`）。
    .filter((c) => !c.platform)
    .filter((c) => !q || c.name.toLowerCase().includes(q) || (c.domain ?? '').toLowerCase().includes(q))
    .slice(0, 30)
})
const mergeTarget = computed(() => store.customers.find((c) => c.id === mergeTargetId.value) ?? null)

function openMerge() {
  mergeQuery.value = ''
  mergeTargetId.value = ''
  mergePreview.value = null
  mergeConfirmName.value = ''
  mergeForceReseller.value = false
  mergeResult.value = null
  mergeOpen.value = true
}
async function pickMergeTarget(id: string) {
  mergeTargetId.value = id
  mergePreview.value = null
  if (!id || !selected.value) return
  mergePreviewBusy.value = true
  try {
    mergePreview.value = await ls.mergePreview(selected.value.id, id)
  } catch (e) {
    await fail(e, '讀取合併預覽失敗')
  } finally {
    mergePreviewBusy.value = false
  }
}
const mergeReady = computed(
  () =>
    !!selected.value &&
    !!mergeTargetId.value &&
    !!mergePreview.value &&
    mergeConfirmName.value.trim() === selected.value.name &&
    (!mergePreview.value.reseller_mismatch || mergeForceReseller.value) &&
    !mergePreview.value.platform_blocked &&
    // 批153：來源是 plugin 發行者＝不能合併（後端 409 `plugin_publisher_customer`）。
    !mergePreview.value.plugin_publisher_blocked &&
    !mergeBusy.value,
)
/** 合併被結構性擋下（平台客戶、來源是 plugin 發行者）——原因在 `warnings`，這裡只決定要不要顯示擋下的那一列。 */
const mergeBlocked = computed(
  () => !!mergePreview.value && (!!mergePreview.value.platform_blocked || !!mergePreview.value.plugin_publisher_blocked),
)
async function doMerge() {
  if (!mergeReady.value || !selected.value) return
  const sourceName = selected.value.name
  mergeBusy.value = true
  try {
    mergeResult.value = await ls.mergeCustomers(mergeTargetId.value, {
      source_id: selected.value.id,
      confirm_name: mergeConfirmName.value.trim(),
      force_reseller: mergeForceReseller.value || undefined,
    })
    // A 已被硬刪 → 選取狀態要放掉，清單與授權快取一起重載。
    selected.value = null
    await Promise.all([reloadList(), store.loadLicenses()])
    await dialogs.notify({
      title: '已合併',
      message: `「${sourceName}」已併入「${mergeTarget.value?.name ?? ''}」。重簽了 ${
        mergeResult.value.resigned_licenses.length
      } 張主證、${mergeResult.value.resigned_addons.length} 張加購；${
        mergeResult.value.airgapped.length
          ? `其中 ${mergeResult.value.airgapped.length} 張在離線站台，需要人工把新證送過去。`
          : '線上站台會在下一次心跳自動換證。'
      }`,
    })
  } catch (e) {
    await fail(e, '合併失敗')
  } finally {
    mergeBusy.value = false
  }
}

// --- 簽發授權（對話框）---
const issueOpen = ref(false)
/**
 * 批93 多產品：核發框最上方的產品下拉。**換產品＝整包重置**（模組鍵、配額鍵、套裝在
 * 不同產品之間完全不通用），並 bump 兩個編輯器的 `:key` 重掛。
 */
const issueProduct = ref('')
const issueSpec = computed(() => store.productByKey(issueProduct.value))
/**
 * 該產品可販售的套裝（`kind='plan'` 且啟用）——不再另打 `/plan-presets`。
 * 批155：內部套裝只有營運主管以上列得出來（後端 403 `internal_plan_requires_manager`）；
 * 換發時現行證本來就是內部套裝的話照列但停用（`planLocked`），否則 chip 一個都不亮。
 */
const issuePlans = computed(() =>
  issuablePlans(issueSpec.value, { internal: auth.isManagerOrAbove, keep: issuePlan.value || null }),
)
function planLocked(p: { internal?: boolean }): boolean {
  return isInternalPlan(p) && !auth.isManagerOrAbove
}
/** 期限下拉的選項（**總分鐘**，批108）＋把當前值併進去（見 `termOptionsWith`）。 */
const issueTermOptions = computed(() => termOptionsWith(issueSpec.value, issue.value.term_total))
const issue = ref({
  /** 期限的**總分鐘**；送出前用 `splitMinutes` 拆成 `term_days`／`term_minutes`。 */
  term_total: 365 * MINUTES_PER_DAY,
  deployment_mode: 'online',
  machine_fingerprint: '',
  host_instance_id: '',
  /**
   * 批110（#5496）：延後生效。空＝立刻生效。
   * 給了它而沒指定到期時刻時，到期日＝這一刻＋期限，**簽發當下就蓋好**
   * （不走「首次取證才起算」）——合約起算日的語意。
   */
  not_before: '',
  /** 批110（#5492）：`on_fetch`＝首次取證才起算（現況）／`at`＝指定到期時刻。 */
  expiry_mode: 'on_fetch' as 'on_fetch' | 'at',
  /** `expiry_mode="at"` 時的到期時刻（合約常寫到當天 23:59）。 */
  expires_at: '',
})
/** 批110：行為參數（#5494 的寬限期就是其中一個鍵）。空＝從套裝模板展開。 */
const issuePolicyValues = ref<Record<string, unknown>>({})
const issueModules = ref<Record<string, string>>({})
// --- 批65：套裝與數量配額 ---
const issuePlan = ref<string>('') // ''＝客製（不掛套裝名）
const issueQuotas = ref<Record<string, number | null> | null>(null)
/**
 * 批114：`QuotaEditor` 回報的「必填的配額鍵都填了嗎」。
 *
 * 必填與「可不可以無上限」由**憑證設計**宣告（不是後台寫死某個鍵）；沒填齊就不讓送出，
 * 否則就是讓人按下去換一個 422 `quotas_invalid` 回來。換產品／套用套裝時編輯器會重掛、
 * 重新回報，所以這裡不必自己重算。
 */
const issueQuotaValid = ref(true)
const quotaKey = ref(0) // QuotaEditor 帶入值後重掛載（同 ModulePicker 的 key 招）
// ModulePicker 重掛載計數（批63：initFrom 只在掛載時跑一次，帶入現值要換 key）。
const issuePickerKey = ref(0)

/**
 * 表單回到該產品的預設值（全模組／客製／配額由 QuotaEditor 依 absent_default 起手）。
 *
 * 「全模組」的鍵集合來自**該產品的憑證設計**（`defaultModulesOf`）。批93 之前是
 * `Object.keys(MODULE_LABELS)`（CS 專用的常數），更早之前是寫死的八鍵——批81 新增
 * `api` 時忘了同步，「新簽發」表單的預設就一直少一個模組。現在鍵集合來自資料。
 *
 * ★ 批114：這裡不再有任何預設數量（以前是寫死的 10）——那是一個產品的數字寫進了後台。
 *   數量一律由 `QuotaEditor` 依憑證設計的 `absent_default` 起手，選了套裝再整包帶入。
 */
function resetIssueForm() {
  issue.value = {
    // 產品宣告的 `default_term_days` → 清單第一個（與後端 `_first_term` 的最後一段同序；
    // 不在清單裡的值 `issueTermOptions` 會併進下拉，不會空白）。
    term_total: defaultTermTotal(issueSpec.value),
    deployment_mode: 'online',
    machine_fingerprint: '', host_instance_id: '',
    not_before: '', expiry_mode: 'on_fetch', expires_at: '',
  }
  issuePolicyValues.value = {}
  issueModules.value = defaultModulesOf(issueSpec.value)
  issuePlan.value = ''
  issueQuotas.value = null
  issuePickerKey.value++
  quotaKey.value++
  // 批114：重掛的編輯器會馬上再回報一次；先收成 true，免得上一張表單留下的 false
  // 讓新表單一開窗按鈕就是灰的。
  issueQuotaValid.value = true
  // break-glass 一律每次重置成關（勾一次不該延續到下一張證）。
  directIssue.value = false
}
/** 換產品＝整包重置（不同產品的模組鍵／配額鍵／套裝完全不通用，沿用會簽出爛證）。 */
function changeIssueProduct(key: string) {
  issueProduct.value = key
  const host = issue.value.host_instance_id
  resetIssueForm()
  issue.value.host_instance_id = host
}
/** 選套裝＝把該套裝一次帶入表單（配額／模組／期限／行為參數），之後仍可逐項手調。 */
function applyPlan(key: string) {
  issuePlan.value = key
  const p = issuePlans.value.find((x) => x.key === key)
  if (!p) return
  if (Object.keys(p.modules).length) { issueModules.value = { ...p.modules }; issuePickerKey.value++ }
  // 批114：套裝的數量也在 `quotas` 裡。舊套裝（migration 之前建的）可能只有一級欄，
  // `seedQuotas` 在 quotas 缺鍵時把它併進來——不然選了套裝反而把數字弄丟。
  issueQuotas.value = seedQuotas(issueSpec.value, p)
  // 批113（#5520）：行為參數也是套裝模板的一部分（`plan_policy` 在後端就是這樣展開的）。
  // 不帶＝選了「標準級」卻停在產品的 `absent_default`，畫面顯示的不是簽出來會有的值。
  issuePolicyValues.value = p.policies ? { ...p.policies } : {}
  // 套裝有指定天數就照套裝（例如試用 90 天）——沒帶的話內容立刻偏離模板，政策被升到
  // superadmin，而畫面上完全看不出是哪一欄不一樣。
  // ★ 批108 truthy 陷阱：`p.term_days` 是 0（分鐘級套裝）時也要帶入。
  if (p.term_days != null || p.term_minutes) {
    issue.value.term_total = totalMinutes(p.term_days, p.term_minutes)
  }
  quotaKey.value++
}
/** 預設產品＝第一個「販售中」的；全部停售時退回清單第一筆（總比空白好）。 */
function defaultProductKey(): string {
  return (store.products.find((p) => p.status === 'active') ?? store.products[0])?.key ?? ''
}
/** 「簽發新授權」：不指定主機、空白表單。 */
function openIssueNew() {
  issueProduct.value = defaultProductKey()
  resetIssueForm()
  issueOpen.value = true
}
/**
 * 從主機列點「核發／換發」：帶入該主機**現有憑證**的設定（批63——換發不再人工回查
 * 配額/模組再重打一次）。無現有憑證（新主機）＝預設值。
 * 期限與行為參數（批108／批113 起主機投影都有）也從現行證起手；舊投影缺欄就維持預設。
 * 帶入值備妥後才開窗（對話框內掛載即吃得到）。
 */
function issueForHost(h: ls.Host) {
  // 換發＝沿用**這張證原本的產品**（換產品等於換一套系統，不會是換發要做的事）。
  issueProduct.value = h.license?.product || defaultProductKey()
  resetIssueForm()
  issue.value.host_instance_id = h.instance_id
  const lic = h.license
  if (lic) {
    issue.value.deployment_mode = lic.deployment_mode || 'online'
    if (lic.modules && Object.keys(lic.modules).length) issueModules.value = { ...lic.modules }
    // 批65：帶入現有套裝與配額（換發沿用、可再調）。
    // 批114：舊證的數量還在一級欄、`quotas` 裡沒有那個鍵 → `seedQuotas` 銜接，
    // 不然一按換發就把客戶的上限改成 `absent_default`。
    issuePlan.value = lic.plan ?? ''
    issueQuotas.value = seedQuotas(issueSpec.value, lic)
    // 批114（#5538）：`{}` 從此是「明確不帶」——換發的行為參數必須從**現行證**起手，
    // 否則操作者沒碰那一區就送出，等於把客戶證上的寬限期靜默拿掉。證上本來就是 `{}`
    // 或舊投影沒有 `policy` 欄時維持空表（送出 `{}`＝不帶；畫面上每個參數都印著
    // 「不帶此參數」，操作者看得到、要帶就自己勾）。
    if (lic.policy) issuePolicyValues.value = { ...lic.policy }
    // ★ 批108 truthy 陷阱：分鐘級憑證 `term_days` 是 0，也要帶。
    if (lic.term_days != null || lic.term_minutes) {
      issue.value.term_total = totalMinutes(lic.term_days ?? 0, lic.term_minutes ?? 0)
    }
  }
  issueOpen.value = true
}

const labelEditing = ref<string | null>(null)
const labelDraft = ref('')
const transferEditing = ref<string | null>(null)
const transferIid = ref('')

function hostName(h: ls.Host): string {
  return h.label || h.hostname || h.instance_id.slice(0, 12)
}
/**
 * 離線授權列的顯示名稱（批114 #5540）。
 *
 * 這些證是**請求檔核發**的，沒有連結列所以沒有標籤也沒有 hostname；綁了機器就印
 * instance 短碼，氣隙證（沒綁機器）印一句話——印空白會讓人以為那一列壞了。
 */
function offlineName(lic: License): string {
  return lic.host_instance_id ? lic.host_instance_id.slice(0, 12) : '氣隙授權（未綁主機）'
}

// --- 主機表的配額欄（批114 裁示 ⑯⑳）----------------------------------------
//
// 批114 之前這裡有五支硬編 helper，畫的是**一個產品**的兩個數字欄（而且第二欄的鍵名
// 直接寫在程式裡）。改成 `<QuotaSummary>`：欄位清單、中文名、用量來源全部由該產品的
// 憑證設計決定，窄欄只顯示前幾項、其餘 `+N`，滑鼠移上去看全部。

/**
 * 顯示用的**有效**配額＝主證 ＋ 目前生效的加購。
 *
 * ★★ 批93-B：只看主證的話，加購提升過上限的站台會顯示舊的分母——同一頁下方的加購
 *   chips 明明寫著已經加購，數字卻沒動。後端無加購時 `effective_quotas` 逐位元等於
 *   `quotas`；`=== undefined` 那一手只是舊回應的相容退路。
 */
function effectiveQuotasOf(lic: License): Record<string, number | null> | null {
  return lic.effective_quotas === undefined ? lic.quotas : lic.effective_quotas
}
/** 這張證有哪些配額鍵是被加購**提升**的（決定要不要畫「＋加購」小標）。 */
function addonQuotaKeys(lic: License | null | undefined): string[] {
  return Object.keys(lic?.addon_sources?.quotas ?? {})
}
/**
 * 「＋加購」小標的 tooltip：逐鍵印「主證原值 → 有效值 ＋ 最早到期」。
 *
 * 文字本體共用 `lib/licenseLabels.ts::quotaAddonHint`（授權詳情也是它）——三態
 * 「未設定／不限／數字」不可混成 `?? '—'`，那會讓人以為前兩者是同一件事。
 */
function addonQuotaHint(h: ls.Host): string {
  const lic = h.license
  if (!lic) return ''
  const spec = store.productByKey(lic.product)
  const eff = effectiveQuotasOf(lic)
  return addonQuotaKeys(lic)
    .map((k) => `${quotaLabel(spec, k)}：${quotaAddonHint(lic, k, eff?.[k] ?? null)}`)
    .join('\n')
}

async function reloadHosts() {
  if (!selected.value) return
  const h = await ls.listCustomerHosts(selected.value.id)
  hosts.value = h.hosts
  offlineLics.value = h.offline_licenses
}

// --- 模組加購子憑證（批93-B）------------------------------------------------
//
// 加購綁的是**主機**不是主證（`licenses` append-only，續約換證加購照樣還在）——所以
// 入口在主機列而不是授權列，chips 也掛在主機上。
const addonHost = ref<ls.Host | null>(null)
const addonOpen = ref(false)

function openAddon(h: ls.Host) {
  addonHost.value = h
  addonOpen.value = true
}

/** 剩餘天數；null＝沒有到期日（尚未蓋章的加購，第一次下發時才蓋）。 */
function addonDaysLeft(a: LicenseAddon): number | null {
  if (!a.expires_at) return null
  return Math.ceil((new Date(a.expires_at).getTime() - Date.now()) / 86400000)
}
function addonChipText(a: LicenseAddon): string {
  const d = addonDaysLeft(a)
  // 尚未蓋章（只有期限）＝證還沒下發過，到期日在第一次下發時才由 LS 蓋上。
  // ★ 批108：期限可以短到分鐘 → 用 `formatTerm` 印「30 分鐘」而不是「0 天」。
  if (d === null) {
    return `${a.label}｜${formatTerm(a.term_days, a.term_minutes)}（首次下發起算）`
  }
  if (d < 0) return `${a.label}（已到期）`
  // 剩餘時間同樣自動選粒度（三個 realm 一致，見 lib/terms.ts）。
  return `${a.label}｜${a.expires_at!.slice(0, 10)}（${formatRemaining(a.expires_at)}）`
}
/**
 * 加購 chip 的 tooltip 要列的模組（批121）：**用憑證設計的中文名**，不是英文鍵。
 *
 * 與 `RequestsView::modulesText` 同一個理由——同一句話裡不要一半中文一半英文鍵名。
 * 名稱查該加購自己那個產品的 spec（`a.product`，不是簽發表單選的產品），查不到就退回鍵名。
 */
function addonModulesText(a: LicenseAddon): string {
  const keys = Object.keys(a.modules ?? {})
  if (!keys.length) return '配額'
  const labels = new Map((store.productByKey(a.product)?.modules ?? []).map((m) => [m.key, m.label]))
  return keys.map((k) => labels.get(k) ?? k).join('、')
}
/** 到期或 7 天內＝黃字（客戶最該注意的那一批）。 */
function addonChipClass(a: LicenseAddon): Record<string, boolean> {
  const d = addonDaysLeft(a)
  return { warn: d !== null && d <= 7 }
}

async function revokeAddon(a: LicenseAddon) {
  if (
    !(await dialogs.confirm({
      title: '撤銷加購',
      message: `撤銷「${a.label}」？下一次心跳送達後，客戶站台的這個加購就會失效。`,
      danger: true,
      confirmText: '撤銷',
    }))
  )
    return
  try {
    const res = await ls.revokeAddon(a.addon_id)
    await reloadHosts()
    if (res.offline_warning) {
      await dialogs.notify({ title: '已撤銷，但請注意', message: res.offline_warning })
    }
  } catch (e) {
    await fail(e, '撤銷加購失敗')
  }
}
/** 客戶詳情（基本資料＋邀請關係，批81 需求 11）。選客戶時載一次。 */
const detail = ref<CustomerDetail | null>(null)

async function selectCustomer(c: Customer) {
  selected.value = c
  custEditing.value = false
  issueOpen.value = false
  detail.value = null
  portalUsers.value = await ls.listCustomerUsers(c.id)
  await Promise.all([
    reloadHosts(),
    ls
      .getCustomer(c.id)
      .then((d) => {
        detail.value = d
      })
      .catch(() => {
        detail.value = null
      }),
  ])
}

/**
 * 基本資料的代填（批97 Bug 7）——表單、驗證與唯讀顯示全在共用元件
 * `components/CustomerProfileCard.vue`（代理商後台用的是同一支）。
 */
async function saveProfile(body: ls.CustomerPatchBody) {
  if (!selected.value) throw new Error('no customer selected')
  return await ls.updateCustomer(selected.value.id, body)
}
/**
 * 存檔成功。回應是 **additive**（客戶投影 ＋ 一個 `profile` 鍵），所以不必再打一次 GET：
 * ① 換掉 `detail`（右卡的唯讀表立刻是新值）
 * ② 換掉 `selected`（`notes`／`kind` 是右卡在讀的欄）
 * ③ `reloadList()`——左清單的「未填資料」標籤與帳號類型過濾是**伺服器**算的，
 *    不重載的話代填完那顆紅標籤還會掛在那裡。
 */
async function onProfileSaved(updated: unknown) {
  const u = updated as Customer & { profile: CustomerProfile }
  if (detail.value) detail.value = { ...detail.value, ...u }
  const merged = { ...(selected.value ?? u), ...u }
  await reloadList()
  selected.value = rows.value.find((c) => c.id === u.id) ?? merged
}
const REFERRER_KIND_LABELS: Record<string, string> = {
  customer: '客戶碼',
  sales: '業務碼',
  campaign: '活動碼',
}
async function addUser() {
  if (!selected.value || !newUserEmail.value.trim()) return
  try {
    await ls.addCustomerUser(selected.value.id, newUserEmail.value.trim())
    newUserEmail.value = ''
    portalUsers.value = await ls.listCustomerUsers(selected.value.id)
  } catch (e) {
    await fail(e, '新增帳號失敗')
  }
}
async function removeUser(u: CustomerUser) {
  if (!selected.value) return
  if (!(await dialogs.confirm({ message: `移除 portal 帳號 ${u.email}？`, danger: true }))) return
  await ls.removeCustomerUser(selected.value.id, u.id)
  portalUsers.value = await ls.listCustomerUsers(selected.value.id)
}
function startLabel(instanceId: string, label: string | null) {
  labelEditing.value = instanceId
  labelDraft.value = label ?? ''
}
/**
 * 存主機標籤。
 *
 * ★ 批114（#5540）：參數從「主機列」改成 `instance_id`——離線授權（請求檔核發的證，
 *   沒有連結列）也走這一支。後端找不到連結列時會**補建一筆無 token 的**再寫標籤，
 *   所以存完重載，那台就會從離線區移進主機表。
 */
async function saveLabel(instanceId: string) {
  if (!selected.value) return
  try {
    await ls.setHostLabel(selected.value.id, instanceId, labelDraft.value.trim() || null)
    labelEditing.value = null
    await reloadHosts()
  } catch (e) {
    await fail(e, '更新標籤失敗')
  }
}
async function doTransfer(h: ls.Host) {
  if (!selected.value || !transferIid.value.trim()) return
  try {
    await ls.transferHost(selected.value.id, h.instance_id, transferIid.value.trim())
    transferEditing.value = null
    transferIid.value = ''
    await Promise.all([reloadHosts(), store.loadLicenses()])
  } catch (e) {
    await fail(e, '轉移失敗')
  }
}
async function doDisableHost(h: ls.Host) {
  if (!selected.value) return
  if (!(await dialogs.confirm({ message: `停用主機「${hostName(h)}」？將撤銷其授權並退役連結（不影響其他主機）。`, danger: true, confirmText: '停用' }))) return
  try {
    await ls.disableHost(selected.value.id, h.instance_id)
    await Promise.all([reloadHosts(), store.loadLicenses()])
  } catch (e) {
    await fail(e, '停用失敗')
  }
}
// --- 批93-C：核發框改走開通流程 ---------------------------------------------
//
// 預設動作從「直接簽發」改成「**建立授權草稿**」：付費授權一律留下申請紀錄與稽核鏈，
// 確認送出時再由政策決定是當場簽發還是等 superadmin 開通。
// 「免審核發（不經申請）」這個勾選**不一定是 break-glass**（批93-C 對齊後端最終判準）：
//
//   政策不放行這個 actor            → 後端 403 `activation_requires_approval`（導向草稿）
//   政策放行、且政策 ≠ superadmin   → 正常直發，`issued_via='policy'`，**不標記、不告警**
//   政策 = superadmin 且 actor 是 superadmin → **破例** `issued_via='direct'` ＋稽核＋告警
//
// 判準是「**這份內容原本需不需要第二個人**」（政策等不等於 superadmin），不是「按的人是誰」
// ——superadmin 滿足每一級政策，用身分判的話 `direct` 永遠走不到。所以勾選的文案、確認框的
// 語氣與按鈕文字都要跟著政策走：對一張 trial 套裝的標準內容大聲警告「這是破例」只會讓人
// 學會忽略警告。
const directIssue = ref(false)
/**
 * 目前表單內容（政策判定用）。
 *
 * ★ 批114：這裡**只有 `quotas`**，沒有任何一級數字欄。`lib/activation.ts` 的比對是
 *   「一級鍵優先、缺了才看 `quotas` 同名鍵」（逐字鏡像後端），所以少送一級鍵不會讓
 *   照模板的內容被誤判成「自訂內容」——多送才會（獨立欄會蓋掉配額裡填的值）。
 */
const issueContent = computed(() => ({
  modules: issueModules.value,
  quotas: issueQuotas.value,
  term_days: splitMinutes(issue.value.term_total)[0],
  term_minutes: splitMinutes(issue.value.term_total)[1],
}))
const issuePolicy = computed(() => policyFor(issueSpec.value, issuePlan.value || null, issueContent.value))
const issueCanDirect = computed(() => canActivateDirectly(auth.role, issuePolicy.value.policy))
const issuePolicyText = computed(() => policyHint(issuePolicy.value, issueCanDirect.value))
/** 這份內容照規矩該走開通流程（政策＝最嚴）→ 勾下去就是**破例**，會被標記並告警。 */
const issueIsBreakGlass = computed(() => issuePolicy.value.policy === 'superadmin')
/** 勾選只有在「這個人現在真的簽得出來」時才該出現；否則後端一定 403。 */
watch(issueCanDirect, (ok) => {
  if (!ok) directIssue.value = false
})

/**
 * 行為參數／啟用時間／到期時刻三個值的**唯一算法**（批113 #5520／#5521）。
 *
 * ★★ 直簽（`doIssue`）與草稿（`draftRequestJson`／`createDraft`）**必須逐字相同**——
 *   兩顆按鈕長在同一個表單裡，算法分岔就是「同一份表單走兩條路簽出兩種證」，
 *   而畫面上完全看不出來。所以抽成三支 helper，三處都呼叫它們。
 */
/**
 * 行為參數：**一律送一份 dict**，空的就是 `{}`（批114 #5538）。
 *
 * ★★ 以前這裡把 `{}` 壓成 `null`，而 `null`／缺鍵在後端的語意是「**未指定** → 從套裝
 *   模板補預設值」。於是「把行為參數的勾選全部取消」這個動作簽出來的證，仍然帶著套裝
 *   預設值（CortexDC 的套裝只有一個鍵，取消它剛好就是全部取消 → 工單 #5538）。
 *   `{}` 才是「明確不帶任何行為參數」，規格 `docs/LICENSE_FORMAT.md` 本來就這樣寫。
 */
function issuePolicyValuesOut(): Record<string, unknown> {
  return { ...issuePolicyValues.value }
}
function issueNotBeforeIso(): string | null {
  return issue.value.not_before ? new Date(issue.value.not_before).toISOString() : null
}
function issueExpiresAtIso(): string | null {
  return issue.value.expiry_mode === 'at' && issue.value.expires_at
    ? new Date(issue.value.expires_at).toISOString()
    : null
}
/** 有填啟用時間／到期時刻＝按鈕上方要先講「走草稿也會帶過去」（批113 走查）。 */
const issueHasTiming = computed(() => !!issueNotBeforeIso() || !!issueExpiresAtIso())

/**
 * 把表單內容包成 `request_json`——後端的申請入口以它為準（指紋與 instance_id 都在裡面），
 * 內容則放 `requested`，與客戶 portal 送上來的形狀一致（`RequestsView` 讀的就是這個鍵）。
 *
 * ★ 批113：`requested` 裡的 policy／not_before／expires_at 是給**人看**的（草稿詳情、
 *   證據檔），後端只信 typed body——`create_license_request` 會把 raw 裡的同名三鍵剃掉。
 *   兩邊帶同一組值（同樣三支 helper）才不會出現「檔案裡看得到卻沒生效」的鍵。
 */
function draftRequestJson(): Record<string, unknown> {
  const fp = issue.value.machine_fingerprint.trim()
  return {
    product: issueProduct.value,
    instance_id: issue.value.host_instance_id || null,
    machine_fingerprint: fp ? { 'machine-id': fp } : null,
    source: 'admin_console',
    requested: {
      plan: issuePlan.value || null,
      modules: { ...issueModules.value },
      // 批114：數量全部在這一包裡（申請內容不再有一級數字欄）。
      quotas: issueQuotas.value,
      term_days: issueContent.value.term_days,
      term_minutes: issueContent.value.term_minutes,
      deployment_mode: issue.value.deployment_mode,
      policy: issuePolicyValuesOut(),
      not_before: issueNotBeforeIso(),
      expires_at: issueExpiresAtIso(),
    },
  }
}

async function createDraft() {
  if (!selected.value) return
  try {
    // 只組一次：typed body 與 `request_json` 是同一份內容的兩個視圖。
    const rj = draftRequestJson()
    await ls.createLicenseRequest({
      customer_id: selected.value.id,
      request_json: rj,
      product: issueProduct.value,
      plan: issuePlan.value || null,
      // ★ 批114：原本這裡還送兩個 `*_requested` 鍵——後端的 `AdminLicenseRequestIn`
      //   根本沒有它們，一直被靜默丟掉（內容是靠 `request_json` 的 `requested` 帶的）。
      //   兩個假鍵已經從型別與呼叫端一起刪掉。
      // 批113：這三個走 typed body 才會被白名單收下（也才拿得到 422 值域校驗）。
      policy: issuePolicyValuesOut(),
      not_before: issueNotBeforeIso(),
      expires_at: issueExpiresAtIso(),
    })
    issueOpen.value = false
    await dialogs.notify({
      title: '已建立草稿',
      message:
        '已在〈授權申請 → 草稿〉建立一筆。到那裡核對內容後按「確認送出」——' +
        (issueCanDirect.value
          ? '依這份內容的套裝政策，確認時會當場簽發。'
          : // ★ 批113（走查第 5 點）：角色名要跟著這份內容的**實際政策**走。
            // 寫死 superadmin 的話，政策其實是營運主管時畫面會叫人去找錯的人。
            `需要${ROLE_LABELS[issuePolicy.value.policy] ?? issuePolicy.value.policy}審核通過後才會核發。`),
    })
  } catch (e) {
    await fail(e, '建立草稿失敗')
  }
}

/**
 * 直簽完成後就地把授權檔存下來（批113 走查第 8 點）。
 *
 * 取原文失敗**不算簽發失敗**——證已經在了，只是這一次沒帶走；所以用 notify 交代
 * 「去授權分頁再下載一次」，不要把人導向「以為沒簽出來」。
 */
async function downloadIssuedLicense(licenseId: string) {
  try {
    const res = await ls.licenseFile(licenseId)
    saveTextFile(res.blob, LICENSE_FILENAME)
    await dialogs.notify({
      title: '已下載憑證',
      message: `已存成 ${LICENSE_FILENAME}。交給客戶放進站台的 license/ 目錄後重啟，或請他貼進站台〈系統 → 授權 → 手動安裝 .lic〉。`,
    })
  } catch (e) {
    await fail(e, '取得憑證失敗（授權已簽發，可到「授權」分頁再下載一次）')
  }
}

async function doIssue() {
  if (!selected.value) return
  // 預設路徑：建草稿。只有 superadmin 顯式勾了 break-glass 才直接簽。
  if (!directIssue.value) {
    await createDraft()
    return
  }
  const ok = await dialogs.confirm({
    title: issueIsBreakGlass.value ? '免審核發（破例）' : '免審核發',
    message: issueIsBreakGlass.value
      ? `這份內容照規矩該走申請審核流程。免審核發會當場簽發一張授權、「不留申請紀錄」，並在稽核與告警上標記為「直接簽發」讓另一位${ROLE_LABELS.superadmin}看到。確定要這樣做嗎？`
      : '依這份內容所選套裝的核發政策，你本來就可以免審核發——會當場簽發一張授權（不留申請紀錄，但不算破例、不發告警）。要繼續嗎？',
    danger: issueIsBreakGlass.value,
    confirmText: '免審核發',
  })
  if (!ok) return
  try {
    const lic = await ls.issueLicense(selected.value.id, {
      product: issueProduct.value,
      modules: issueModules.value,
      term_days: issueContent.value.term_days,
      term_minutes: issueContent.value.term_minutes,
      deployment_mode: issue.value.deployment_mode,
      machine_fingerprint: issue.value.machine_fingerprint.trim()
        ? { 'machine-id': issue.value.machine_fingerprint.trim() }
        : null,
      host_instance_id: issue.value.host_instance_id || null,
      // 批65：套裝名快照（''＝客製→null）＋數量配額（QuotaEditor 一律每鍵齊帶）。
      // 批114：所有數量都在這一包裡——表單已經沒有獨立的數字欄。
      plan: issuePlan.value || null,
      quotas: issueQuotas.value,
      // 批110：三者的交互——同時給啟用時間與到期時刻時**以到期時刻為準**；
      // 只給啟用時間就是「那一刻＋期限」；都不給維持現行的首次取證才蓋章。
      // ★ 批113：三支 helper 與草稿路徑共用（見 `issuePolicyValuesOut` 上方的說明）。
      policy: issuePolicyValuesOut(),
      not_before: issueNotBeforeIso(),
      expiry_mode: issue.value.expiry_mode,
      expires_at: issueExpiresAtIso(),
    })
    issueOpen.value = false
    await Promise.all([reloadHosts(), store.loadLicenses()])
    // 警告級：鍵打錯不擋簽發，但要讓操作者看到（對話框已關，附在完成通知裡）。
    // 批93 三欄：模組鍵／配額鍵／**scoped 子項值**（例 `channel=line,slack` 的 slack）。
    const warns: string[] = []
    if (lic.unknown_modules?.length) {
      warns.push(`模組代號：${lic.unknown_modules.join('、')}（產品端「代號缺席＝該模組停用」）`)
    }
    if (lic.unknown_quota_keys?.length) warns.push(`配額代號：${lic.unknown_quota_keys.join('、')}`)
    if (lic.unknown_module_items?.length) {
      warns.push(`模組子項：${lic.unknown_module_items.join('、')}`)
    }
    const warn = warns.length
      ? `\n\n⚠ 授權內含這個產品憑證設計不認得的內容——\n・${warns.join(`\n・`)}\n請確認不是打錯字。`
      : ''
    const directTag = lic.direct
      ? `\n\n⚑ 這張標記為「直接簽發」（未經申請流程），已記入稽核並通知其他${ROLE_LABELS.superadmin}。`
      : ''
    // 批113（走查第 8 點）：簽完最常做的下一件事就是把證交給客戶——別叫人再跑一趟
    // 「授權」分頁。`dialogs` 只有 confirm／notify 兩種，所以用 confirm 當二選一。
    // ★ 沒指定啟用／到期時刻的證，「下載」就是 `materialize_expiry` 蓋章起算的那一刻
    //   （既有七出口規則）——所以要先講，並且讓「稍後」是個正常選項。
    const stampNote = issueHasTiming.value
      ? ''
      : '（這張的到期日在第一次取回時才起算——現在下載就是從此刻起算）'
    const wantFile = await dialogs.confirm({
      title: '已簽發',
      message:
        `授權 ${lic.license_id.slice(0, 8)} 已建立，可至「授權」分頁查看 / 下載。` +
        warn +
        directTag +
        `\n\n要現在下載授權檔交給客戶嗎？${stampNote}`,
      confirmText: '立即下載',
      cancelText: '稍後',
    })
    if (wantFile) await downloadIssuedLicense(lic.license_id)
  } catch (e) {
    // 政策不放行且不是 superadmin → 後端 403 `activation_requires_approval`：
    // 這不是錯誤，是流程指引——直接把人導向「建立草稿」那條路。
    const detail =
      e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
    const code =
      typeof detail === 'string' ? detail : ((detail as { code?: string } | undefined)?.code ?? '')
    if (e instanceof ApiError && e.status === 403 && code === 'activation_requires_approval') {
      const ok = await dialogs.confirm({
        title: '這份內容需要審核',
        message:
          '依這份內容的套裝核發政策，不能直接簽發。要改成「建立授權草稿」嗎？確認送出後由有權限的人審核核發。',
        confirmText: '建立草稿',
      })
      if (ok) await createDraft()
      return
    }
    await fail(e, '簽發失敗')
  }
}
</script>

<template>
  <div class="page">
    <div class="page-head"><h1>客戶</h1></div>
    <div class="grid">
      <!-- 左：客戶清單 -->
      <div class="card">
        <div class="create-row">
          <input v-model="newName" placeholder="客戶名稱" v-enter="createCustomer" />
          <UiButton variant="primary" @click="createCustomer">新增</UiButton>
        </div>
        <input v-model="newDomain" class="domain" placeholder="email 網域（選填，如 acme.com）" />
        <p class="domain-hint">填網域後，同網域的 Google email 首次登入 portal 會自動歸到此客戶（免逐一授權帳號）。</p>
        <!-- 批81 需求 11：帳號類型過濾（`unset`＝還沒補填基本資料的舊客戶）。 -->
        <select v-model="kindFilter" class="kind-sel" @change="reloadList">
          <option value="">全部帳號類型</option>
          <option value="company">公司／組織</option>
          <option value="personal">個人</option>
          <option value="unset">尚未填寫</option>
        </select>
        <!-- 批93-C：歸屬代理商過濾。`none`＝直營（`reseller_id IS NULL`），是伺服器端條件。 -->
        <select v-model="resellerFilter" class="kind-sel" @change="reloadList">
          <option value="">全部歸屬</option>
          <option value="none">直營（未掛代理商）</option>
          <option v-for="r in resellerOptions" :key="r.id" :value="r.id">{{ r.name }}</option>
        </select>
        <!-- 批141：來源篩選（合作平台的組織含已解除關聯的）。 -->
        <select v-model="sourceFilter" class="kind-sel" @change="reloadList">
          <option value="all">全部來源</option>
          <option value="direct">非平台客戶</option>
          <option value="platform">合作平台的組織</option>
        </select>
        <div class="empty-row">
          <label class="empty-opt" title="無授權、無申請、基本資料未填的組織——不自動歸戶之後會長出來的殘渣">
            <input v-model="emptyOnly" type="checkbox" @change="reloadList" />
            只看空組織
          </label>
          <FilterClearButton size="sm" :dirty="filtersDirty" @click="clearFilters" />
        </div>
        <ul class="list">
          <li v-for="c in rows" :key="c.id" :class="{ sel: selected?.id === c.id }" @click="selectCustomer(c)">
            {{ c.name }} <small v-if="c.domain" class="dom">@{{ c.domain }}</small>
            <!-- 掛了代理商才標；直營不標（多數客戶都是直營，全部標等於沒標）。 -->
            <small v-if="c.reseller_id" class="rs-tag">{{ resellerOf(c) }}</small>
            <!-- 批141：合作平台的組織（已解除的照樣標，灰一點）。 -->
            <small
              v-if="c.platform"
              class="plat-tag"
              :class="{ gone: !!c.platform.detached_at }"
              :title="platformNote(c) ?? ''"
            >平台・{{ c.platform.name }}</small>
            <!-- 批141-R：「未填資料」是在催客戶上 portal 補填——平台客戶不開 portal，不標。 -->
            <small v-if="!c.profile_completed_at && !c.platform" class="unset-tag">未填資料</small>
            <small v-else-if="c.referral_count" class="ref-tag">邀 {{ c.referral_count }}</small>
          </li>
        </ul>
        <p v-if="!rows.length && listFiltered" class="subtle">這個條件下沒有客戶。</p>
        <!-- keyset 分頁：後端一頁 50 筆（未過濾時 200，見 store）。有游標才畫這一列。 -->
        <div v-if="nextCursor" class="more">
          <UiButton size="sm" :loading="loadingMore" @click="loadMore">載入更多</UiButton>
          <span class="subtle">已載入 {{ rows.length }} 家</span>
        </div>
      </div>

      <!-- 右：明細 -->
      <div v-if="selected" class="card">
        <div class="cust-head">
          <template v-if="custEditing">
            <input
              v-model="custName"
              class="cust-in"
              placeholder="客戶名稱"
              v-enter="saveCustomer"
              @keyup.esc="custEditing = false"
            />
            <input
              v-model="custDomain"
              class="cust-in"
              placeholder="email 網域（選填，如 acme.com）"
              v-enter="saveCustomer"
              @keyup.esc="custEditing = false"
            />
            <UiButton variant="primary" size="sm" @click="saveCustomer">存</UiButton>
            <UiButton size="sm" @click="custEditing = false">取消</UiButton>
          </template>
          <template v-else>
            <h2 class="cust-name">{{ selected.name }}</h2>
            <small v-if="selected.domain" class="dom">@{{ selected.domain }}</small>
            <button class="x" title="修改名稱／網域" @click="startEditCustomer">✎</button>
            <UiButton
              v-if="auth.isManagerOrAbove"
              variant="secondary"
              size="sm"
              class="del-btn"
              :disabled="!!selectedPlatformNote"
              :title="selectedPlatformNote ?? '把這個客戶併入另一個客戶（成員／授權／申請全部搬過去，本客戶會被刪除）'"
              @click="openMerge"
            >
              合併到其他客戶…
            </UiButton>
            <UiButton
              v-if="auth.isManagerOrAbove"
              variant="danger"
              size="sm"
              :disabled="!!selectedPlatformNote"
              :title="selectedPlatformNote ?? ''"
              @click="openDelete"
            >
              刪除客戶
            </UiButton>
          </template>
        </div>
        <!-- 批141：平台客戶的說明（刪除／合併／portal 帳號都停用的原因）。 -->
        <p v-if="selectedPlatformNote" class="plat-note">{{ selectedPlatformNote }}</p>

        <!-- 批93-C：歸屬代理商（與「邀請歸因」並列的兩件事——歸因是行銷，歸屬是契約） -->
        <div class="assign-line">
          <span class="ref-k">歸屬代理商</span>
          <span v-if="!assignOpen" class="assign-val">
            {{ resellerOf(selected) }}
            <!-- 批141：平台客戶不能指派代理商（後端 409 `platform_managed_customer`）。 -->
            <button
              v-if="auth.isAdminOrAbove && !selected.platform"
              class="x"
              title="指派／改派歸屬代理商"
              @click="startAssign"
            >
              ✎
            </button>
            <small v-else-if="auth.isAdminOrAbove" class="subtle">（合作平台的組織不能指派代理商）</small>
          </span>
          <template v-else>
            <select v-model="assignTarget" class="sm">
              <option value="">直營（不掛代理商）</option>
              <option v-for="r in resellerOptions" :key="r.id" :value="r.id">{{ r.name }}</option>
            </select>
            <UiButton variant="primary" size="sm" :loading="assignBusy" @click="saveAssign">存</UiButton>
            <UiButton size="sm" @click="assignOpen = false">取消</UiButton>
            <span class="subtle">
              改派不影響既有授權上的經手紀錄；續約時才會以新歸屬為準。
            </span>
          </template>
        </div>

        <!-- 基本資料（批97 Bug 7）：唯讀 ＋ 我方代填，共用元件（代理商後台同一支） -->
        <p v-if="!detail" class="subtle">載入中…</p>
        <template v-else>
          <CustomerProfileCard
            :profile="detail.profile"
            :notes="selected.notes"
            :can-edit="auth.isAdminOrAbove"
            :load-options="ls.getCustomerProfileOptions"
            :save="saveProfile"
            :managed-by-platform="selected.platform?.name ?? null"
            @saved="onProfileSaved"
          />

          <!-- 邀請關係：誰帶他進來的 ＋ 他帶了誰。
               批141-R：平台客戶不開 portal，邀請碼用不到（也邀不了人）→ 整塊不出現。 -->
          <div v-if="!selected.platform" class="ref-block">
            <div class="ref-line">
              <span class="ref-k">自己的邀請碼</span>
              <template v-if="detail.own_code">
                <code class="mono">{{ detail.own_code.code }}</code>
                <span class="status-pill" :class="detail.own_code.is_active ? 'active' : 'revoked'">
                  {{ detail.own_code.is_active ? '啟用中' : '已停用' }}
                </span>
              </template>
              <span v-else class="subtle">—</span>
            </div>
            <div class="ref-line">
              <span class="ref-k">邀請人</span>
              <template v-if="detail.referred_by">
                <span v-if="detail.referred_by.customer_id" class="lnk-static">
                  {{ detail.referred_by.customer_name ?? '—' }}
                </span>
                <span v-else>{{ detail.referred_by.label ?? '—' }}</span>
                <span class="chip">{{ REFERRER_KIND_LABELS[detail.referred_by.kind] ?? detail.referred_by.kind }}</span>
                <code class="mono">{{ detail.referred_by.code }}</code>
              </template>
              <span v-else class="subtle">無（自行註冊）</span>
            </div>
            <div class="ref-line">
              <span class="ref-k">已邀請</span>
              <span>{{ detail.referral_count }} 位</span>
            </div>
            <ul v-if="detail.referrals.length" class="chips">
              <li v-for="r in detail.referrals" :key="r.customer_id" class="chip">
                {{ r.customer_name }}<small class="subtle">（{{ r.created_at.slice(0, 10) }}）</small>
              </li>
            </ul>
          </div>
        </template>

        <h3 class="section-title">Portal 登入帳號（預授權 Google email）</h3>
        <!-- 批141（裁示 24）：平台客戶不給 portal——不能授權登入帳號（後端 409 `platform_managed_customer`）。 -->
        <p v-if="selected.platform" class="subtle">合作平台的組織不使用客戶 portal，不能授權登入帳號。</p>
        <div v-else class="field-row">
          <input v-model="newUserEmail" placeholder="customer@email.com" v-enter="addUser" />
          <UiButton @click="addUser">授權</UiButton>
        </div>
        <ul class="chips">
          <li v-for="u in portalUsers" :key="u.id" class="chip">
            {{ u.email }} <button class="x" @click="removeUser(u)">×</button>
          </li>
        </ul>

        <div class="sec-head">
          <h3 class="section-title">
            主機（{{ hosts.length + offlineLics.length }}）— 一主機一張獨立授權
          </h3>
          <UiButton variant="primary" size="sm" @click="openIssueNew">簽發新授權</UiButton>
        </div>
        <p v-if="!hosts.length && !offlineLics.length" class="subtle">
          尚無主機與授權。主機以 OAuth 連結後會出現於此，再對該主機核發授權；離線（氣隙）站台
          用「簽發新授權」或請求檔核發，簽出來的證同樣列在這裡。
        </p>
        <div v-if="hosts.length || offlineLics.length" class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead><tr><th>主機</th><th>授權</th><th>配額</th><th>到期</th><th>最後上線</th><th></th></tr></thead>
            <tbody>
              <tr v-for="h in hosts" :key="h.instance_id">
                <td>
                  <template v-if="labelEditing === h.instance_id">
                    <input
                      v-model="labelDraft"
                      placeholder="主機標籤"
                      v-enter="() => saveLabel(h.instance_id)"
                    />
                  </template>
                  <template v-else>
                    <strong>{{ hostName(h) }}</strong>
                    <button class="x" title="改標籤" @click="startLabel(h.instance_id, h.label)">✎</button>
                    <div><code class="mono">{{ h.instance_id.slice(0, 12) }}</code>
                      <span v-if="h.conflict_flag" class="status-pill revoked">衝突</span>
                    </div>
                    <!-- 批93-B：加購 chips（綁主機不綁證，所以掛在這一格）。 -->
                    <div v-if="h.addons?.length" class="addon-chips">
                      <span
                        v-for="a in h.addons"
                        :key="a.addon_id"
                        class="chip addon"
                        :class="addonChipClass(a)"
                        :title="`加購 ${a.label}｜${addonModulesText(a)}`"
                      >
                        ＋{{ addonChipText(a) }}
                        <button
                          v-if="auth.isManagerOrAbove"
                          type="button"
                          class="x"
                          title="撤銷這張加購"
                          @click="revokeAddon(a)"
                        >
                          ×
                        </button>
                      </span>
                    </div>
                  </template>
                </td>
                <td>
                  <span v-if="h.license" class="status-pill" :class="effectiveStatus(h.license)">
                    {{ statusLabel(effectiveStatus(h.license)) }}
                  </span>
                  <span v-else class="status-pill pending">待發</span>
                </td>
                <!-- 批114 裁示 ⑯⑳：全部配額的精簡摘要（前幾項＋`+N`，游標移上去看全部）。
                     用量只在這台回報過心跳時才給——沒有部署列時後端的 `*_in_use` 是 0，
                     直接畫「0 / 上限」會讓人以為它上線過。 -->
                <td class="quota">
                  <QuotaSummary
                    v-if="h.license"
                    :spec="store.productByKey(h.license.product)"
                    :quotas="effectiveQuotasOf(h.license)"
                    :usage="h.last_seen ? h : null"
                  />
                  <span v-else class="subtle">—</span>
                  <small
                    v-if="addonQuotaKeys(h.license).length"
                    class="ad-tag"
                    :title="addonQuotaHint(h)"
                    >＋加購</small
                  >
                </td>
                <td>{{ h.license ? fmt(h.license.expires_at) : '—' }}</td>
                <td class="seen" :class="staleLevel(h.last_seen)">{{ fmtSeen(h.last_seen) }}</td>
                <td class="acts-cell">
                  <div class="acts">
                    <template v-if="labelEditing === h.instance_id">
                      <UiButton variant="primary" size="sm" @click="saveLabel(h.instance_id)">存</UiButton>
                      <UiButton size="sm" @click="labelEditing = null">取消</UiButton>
                    </template>
                    <template v-else-if="transferEditing === h.instance_id">
                      <input v-model="transferIid" class="sm" placeholder="新 instance_id" />
                      <UiButton variant="primary" size="sm" @click="doTransfer(h)">確定</UiButton>
                      <UiButton size="sm" @click="transferEditing = null">取消</UiButton>
                    </template>
                    <template v-else>
                      <UiButton v-if="!h.license" variant="primary" size="sm" @click="issueForHost(h)">核發</UiButton>
                      <UiButton v-if="h.license" size="sm" @click="issueForHost(h)">換發</UiButton>
                      <!-- ★ 代管站不給轉移（後端 403）：換機換的是主機錨點、instance_id 不變，
                           正解是代管主機頁的「換機：整批重簽授權」。 -->
                      <UiButton v-if="h.license && !h.hosted" size="sm" @click="transferEditing = h.instance_id">
                        轉移
                      </UiButton>
                      <!-- 批93-B：加購（子憑證）。後端是 `require_admin` ＋政策閘（93-C 改政策驅動），
                           UI 這裡保守一級；批110 起以營運主管為界。 -->
                      <UiButton
                        v-if="auth.isManagerOrAbove"
                        size="sm"
                        title="在既有套裝之外單獨加一個模組／一段配額（獨立子憑證，可單獨撤銷）"
                        @click="openAddon(h)"
                      >
                        加購
                      </UiButton>
                      <UiButton v-if="h.license" variant="danger" size="sm" @click="doDisableHost(h)">停用</UiButton>
                    </template>
                  </div>
                </td>
              </tr>

              <!-- 批114（#5540）：**離線授權**——請求檔核發的證沒有連結列，所以不在
                   `hosts` 裡。以前這裡只在表格下方印一行「N 張，見授權分頁」，結果客戶
                   已經生效的站台在這一頁完全看不到，標籤也就改不了。
                   綁了機器的列可以就地改標籤（後端會補建一筆無 token 的連結列，存完
                   重載就會移到上面的主機區）；氣隙證沒有機器可綁，唯讀。 -->
              <tr v-for="lic in offlineLics" :key="lic.license_id" class="offline-row">
                <td>
                  <template v-if="lic.host_instance_id && labelEditing === lic.host_instance_id">
                    <input
                      v-model="labelDraft"
                      placeholder="主機標籤"
                      v-enter="() => saveLabel(lic.host_instance_id!)"
                    />
                  </template>
                  <template v-else>
                    <strong>{{ offlineName(lic) }}</strong>
                    <button
                      v-if="lic.host_instance_id"
                      class="x"
                      title="改標籤（會把這台列入上方主機清單）"
                      @click="startLabel(lic.host_instance_id, null)"
                    >
                      ✎
                    </button>
                    <div>
                      <span class="off-tag" title="這張證沒有連結主機：離線／氣隙站台或請求檔核發">
                        離線
                      </span>
                    </div>
                  </template>
                </td>
                <td>
                  <span class="status-pill" :class="effectiveStatus(lic)">
                    {{ statusLabel(effectiveStatus(lic)) }}
                  </span>
                </td>
                <!-- 離線證沒有心跳＝沒有用量，只顯示上限（裁示 ⑳）。 -->
                <td class="quota">
                  <QuotaSummary
                    :spec="store.productByKey(lic.product)"
                    :quotas="effectiveQuotasOf(lic)"
                  />
                </td>
                <td>{{ fmt(lic.expires_at) }}</td>
                <td class="seen"><span class="subtle">—</span></td>
                <td class="acts-cell">
                  <div class="acts">
                    <template v-if="lic.host_instance_id && labelEditing === lic.host_instance_id">
                      <UiButton variant="primary" size="sm" @click="saveLabel(lic.host_instance_id)">
                        存
                      </UiButton>
                      <UiButton size="sm" @click="labelEditing = null">取消</UiButton>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 授權草稿：對話框（批93-C 起預設走申請審核流程，不再直接簽發） -->
        <UiModal
          :open="issueOpen"
          :title="`建立授權草稿 — ${selected.name}`"
          @close="issueOpen = false"
        >
          <!-- 批93 多產品：產品是這張表單的**第一個決定**——模組鍵、配額鍵、套裝全部跟著它換。 -->
          <label class="field prod-field">
            <span>產品</span>
            <select :value="issueProduct" @change="changeIssueProduct(($event.target as HTMLSelectElement).value)">
              <option v-for="p in store.products" :key="p.key" :value="p.key">
                {{ p.name }}{{ p.status === 'active' ? '' : '（已停售）' }}
              </option>
            </select>
          </label>
          <p v-if="!store.products.length" class="subtle">
            還沒有任何產品——請先到〈系統 → 產品〉建立一個並定義它的憑證設計。
          </p>
          <!-- 批65：授權套裝——選擇即自動帶入配額／模組／期限／行為參數（可再逐項手調）。 -->
          <div class="plan-row">
            <span class="plan-label">套裝</span>
            <button
              v-for="p in issuePlans"
              :key="p.key"
              type="button"
              class="plan-chip"
              :class="{ on: issuePlan === p.key }"
              :disabled="planLocked(p)"
              :title="planLocked(p) ? '內部套裝：只有營運主管以上能選' : undefined"
              @click="applyPlan(p.key)"
            >
              {{ p.label }}<span v-if="isInternalPlan(p)" class="int-mark">內部</span>
            </button>
            <button
              type="button"
              class="plan-chip"
              :class="{ on: issuePlan === '' }"
              @click="issuePlan = ''"
            >
              客製
            </button>
          </div>
          <div class="form">
            <label class="field"><span>核發給主機</span>
              <select v-model="issue.host_instance_id">
                <option value="">不指定（離線 / 氣隙）</option>
                <option v-for="h in hosts" :key="h.instance_id" :value="h.instance_id">
                  {{ hostName(h) }}{{ h.license ? '（換發）' : '（待發）' }}
                </option>
              </select>
            </label>
            <!-- 批114：獨立的數字欄拔掉了——數量一律在下面〈數量配額〉裡填，欄位清單、
                 必填與否全部由該產品的憑證設計決定。 -->
            <label class="field"><span>期限</span>
              <!-- 批108：選項與自由輸入都是**總分鐘**（產品的 `term_options`；
                   缺席才從舊的 `term_day_options` ×1440 換算）。 -->
              <select v-model.number="issue.term_total">
                <option v-for="m in issueTermOptions" :key="m" :value="m">{{ termLabel(m) }}</option>
              </select>
              <TermField v-model="issue.term_total" />
            </label>
            <label v-if="optionOf(issueSpec, 'supports_deployment_mode')" class="field"><span>模式</span>
              <select v-model="issue.deployment_mode"><option value="online">線上</option><option value="airgapped">離線（氣隙）</option></select>
            </label>
            <label class="field"><span>綁定指紋（選填）</span><input v-model="issue.machine_fingerprint" placeholder="16 hex 或留空" /></label>
            <!-- 批110（#5496）：合約起算日。站台在那之前繼續用手上那張證。 -->
            <label class="field"><span>啟用時間（選填）</span><input v-model="issue.not_before" type="datetime-local" /></label>
            <!-- 批110（#5492）：合約寫死迄日時用這個（例如 23:59）。 -->
            <label class="field"><span>到期方式</span>
              <select v-model="issue.expiry_mode">
                <option value="on_fetch">依期限（首次取證才起算）</option>
                <option value="at">指定到期時刻</option>
              </select>
            </label>
            <label v-if="issue.expiry_mode === 'at'" class="field"><span>到期時刻</span><input v-model="issue.expires_at" type="datetime-local" /></label>
          </div>
          <!-- :key 強制重掛載（批63）：issueForHost 帶入現有憑證模組時 initFrom 才會反映到 UI。 -->
          <!-- :key 兼顧兩件事：帶入現值（批63）與**換產品**（批93）都必須重掛編輯器。 -->
          <div class="mp">
            <ModulePicker
              :key="`${issueProduct}-${issuePickerKey}`"
              v-model="issueModules"
              :spec="issueSpec"
              :allow-internal="auth.isManagerOrAbove"
            />
          </div>
          <!-- 批65：數量配額（欄位由該產品的憑證設計決定；每欄可勾「無上限」）。
               批114：這張表**唯一**填數量的地方——必填鍵沒填會在該列畫紅字，
               並經 `update:valid` 把下面的按鈕反灰（送出去也只會換一個 422 回來）。 -->
          <h4 class="quota-title">數量配額</h4>
          <div class="mp">
            <QuotaEditor
              :key="`${issueProduct}-${quotaKey}`"
              v-model="issueQuotas"
              :spec="issueSpec"
              @update:valid="issueQuotaValid = $event"
            />
          </div>
          <!-- 批110：行為參數（這個產品有宣告才出現；#5494 的寬限期就是一個 policy 鍵）。 -->
          <template v-if="(issueSpec?.policies?.length ?? 0) > 0">
            <h4 class="quota-title">行為參數</h4>
            <div class="mp">
              <PolicyEditor :key="`${issueProduct}-${quotaKey}`" v-model="issuePolicyValues" :spec="issueSpec" />
            </div>
          </template>
          <p v-if="issue.expiry_mode === 'at'" class="subtle">
            到期時刻已指定——這張證簽出來就帶著它，不再依期限起算。
          </p>
          <p v-else-if="issue.not_before" class="subtle">
            期限從**啟用時間**起算，到期日在簽發當下就定好（不等首次取證）。
          </p>
          <p v-else class="subtle">到期日於客戶第一次下載/取回時依當下時間起算。</p>

          <!-- 批93-C：政策提示＋break-glass。預設路徑是建草稿，這一段是在說「之後會怎樣」。 -->
          <p class="policy" :class="{ ok: issueCanDirect }">
            <strong>{{ activationPolicyLabel(issuePolicy.policy) }}</strong>
            <span v-if="issuePolicyText"> — {{ issuePolicyText }}</span>
          </p>
          <!-- 只在「這個人依政策現在簽得出來」時才出現（否則按下去必定 403）。
               文案分兩種：政策＝superadmin 才是破例，其餘是政策允許的正常直發。 -->
          <label v-if="issueCanDirect" class="direct-chk" :class="{ glass: issueIsBreakGlass }">
            <input v-model="directIssue" type="checkbox" />
            {{ issueIsBreakGlass ? '免審核發（破例，不經申請流程）' : '免審核發（不經申請流程）' }}
            <span class="subtle">
              <template v-if="issueIsBreakGlass">
                當場簽發、<strong>不留申請紀錄</strong>；會標記為「直接簽發」並通知其他{{ ROLE_LABELS.superadmin }}。
                只在對方等著上線、來不及跑流程時用。
              </template>
              <template v-else>
                <!-- ★ 批113（走查第 6 點）：template 純文字不吃 Markdown，`**…**` 會原樣
                     印出兩顆星號。要粗體就用 `<strong>`。 -->
                依這份內容所選套裝的核發政策，你可以免審核發——當場簽發、<strong>不留申請紀錄</strong>，
                但<strong>不算破例</strong>（不標記、不發告警）。想留下申請紀錄就別勾。
              </template>
            </span>
          </label>
          <!-- 批113（#5521）：這兩個時刻以前只有直簽帶得走，走草稿會被靜默丟掉——
               現在兩條路都會帶，講清楚才不用讓人猜。 -->
          <p v-if="issueHasTiming && !directIssue" class="subtle timing-note">
            走草稿也會把<strong>啟用時間／到期時刻</strong>一併寫進草稿，核發時照這份簽。
          </p>
          <!-- 批114：按鈕反灰的理由要寫出來，不然只會看到「按了沒反應」（哪一欄的紅字
               在上面那張配額表裡）。 -->
          <p v-if="!issueQuotaValid" class="warn-text">
            〈數量配額〉還有必填的項目沒填，填完才能送出。
          </p>

          <template #foot>
            <UiButton @click="issueOpen = false">取消</UiButton>
            <UiButton variant="primary" :disabled="!issueQuotaValid" @click="doIssue">
              {{ directIssue ? (issueIsBreakGlass ? '免審核發（破例）' : '免審核發') : '建立草稿' }}
            </UiButton>
          </template>
        </UiModal>

        <!-- 合併客戶 A→B（superadmin）：搜尋 B → 預覽 → 輸入 A 名稱確認 → 結果 -->
        <UiModal :open="mergeOpen" width="640px" title="合併客戶" @close="mergeOpen = false">
          <template v-if="mergeResult">
            <p class="merge-done">
              已把「{{ selected?.name ?? '' }}」併入「{{ mergeTarget?.name ?? '' }}」。
            </p>
            <ul class="del-list">
              <li v-for="(n, k) in mergeResult.moved" :key="k">{{ k }}：搬移 <strong>{{ n }}</strong> 筆</li>
              <li>重簽主證 <strong>{{ mergeResult.resigned_licenses.length }}</strong> 張</li>
              <li>重簽加購 <strong>{{ mergeResult.resigned_addons.length }}</strong> 張</li>
            </ul>
            <p v-if="mergeResult.airgapped.length" class="del-block">
              ⚠ 這 {{ mergeResult.airgapped.length }} 張在<strong>離線（氣隙）</strong>站台，不會靠心跳換證——
              請到「授權」分頁下載新證，人工送到客戶手上：
              <code v-for="id in mergeResult.airgapped" :key="id" class="mono">{{ id.slice(0, 8) }}</code>
            </p>
            <p v-for="w in mergeResult.warnings" :key="w" class="warn-text">⚠ {{ w }}</p>
          </template>
          <template v-else-if="selected">
            <p class="subtle">
              把 <strong>{{ selected.name }}</strong> 併入另一個客戶：成員、授權、申請、代管站全部搬過去，
              <strong>{{ selected.name }} 會被永久刪除</strong>（快照留在稽核紀錄）。
              生效中的授權會<strong>同內容重簽</strong>到新客戶名下（線上站台下一次心跳自動換證）。
            </p>
            <label class="field">
              <span>併入哪一個客戶</span>
              <input v-model="mergeQuery" placeholder="輸入名稱或網域搜尋…" />
            </label>
            <ul class="merge-list">
              <li
                v-for="c in mergeCandidates"
                :key="c.id"
                :class="{ sel: mergeTargetId === c.id }"
                @click="pickMergeTarget(c.id)"
              >
                {{ c.name }}
                <small v-if="c.domain" class="dom">@{{ c.domain }}</small>
                <small v-if="c.reseller_id" class="rs-tag">{{ resellerOf(c) }}</small>
              </li>
            </ul>

            <div v-if="mergePreviewBusy" class="loading-wrap"><span class="spinner" /></div>
            <template v-else-if="mergePreview">
              <h4 class="sub-title">會發生什麼</h4>
              <ul class="del-list">
                <li>成員 <strong>{{ mergePreview.members }}</strong> 位搬移</li>
                <li>
                  授權 <strong>{{ mergePreview.licenses }}</strong> 張（其中
                  <strong>{{ mergePreview.resign_count }}</strong> 張要重簽、
                  <strong>{{ mergePreview.airgapped }}</strong> 張在離線站台需人工送檔）
                </li>
                <li>申請 <strong>{{ mergePreview.requests }}</strong> 筆、代管站 <strong>{{ mergePreview.hosted_sites }}</strong> 個</li>
              </ul>
              <!-- 批153：注意事項以後端的 `warnings` 為準（代理商不同、網域衝突、重複帳號、平台客戶、
                   plugin 發行者）；舊後端沒帶 `warnings` 才退回前端自己組的句子。 -->
              <template v-if="mergePreview.warnings">
                <p v-for="w in mergePreview.warnings" :key="w" class="warn-text" data-testid="merge-warning">⚠ {{ w }}</p>
              </template>
              <template v-else>
                <p v-if="mergePreview.duplicate_members.length" class="warn-text">
                  ⚠ 這些 email 兩邊都有，會保留目標客戶的那一列：
                  {{ mergePreview.duplicate_members.join('、') }}
                </p>
                <p v-if="mergePreview.domain_conflict" class="warn-text">
                  ⚠ 兩邊都有網域且不同——目標客戶的網域保留，來源的會寫進備註。
                </p>
                <!-- 批141：任一方是平台客戶＝不能合併（後端 409 `platform_managed_customer`）。 -->
                <p v-if="mergePreview.platform_blocked" class="warn-text">⚠ 合併的任一方是合作平台的組織，不能合併。</p>
              </template>
              <!-- 平台客戶、來源是 plugin 發行者：結構性擋下（後端 409），原因已在上方的注意事項。 -->
              <p v-if="mergeBlocked" class="del-block" data-testid="merge-blocked">
                ⚠ 這兩個客戶不能合併，〈執行合併〉已停用。
              </p>
              <label v-if="mergePreview.reseller_mismatch" class="chk warn-chk">
                <input v-model="mergeForceReseller" type="checkbox" />
                兩邊的歸屬代理商不同，仍要合併（以目標客戶的歸屬為準，並記入稽核）
              </label>
              <label class="field">
                <span>請輸入來源客戶名稱「{{ selected.name }}」以確認</span>
                <input v-model="mergeConfirmName" :placeholder="selected.name" />
              </label>
            </template>
          </template>
          <template #foot>
            <UiButton @click="mergeOpen = false">{{ mergeResult ? '關閉' : '取消' }}</UiButton>
            <UiButton
              v-if="!mergeResult"
              variant="danger"
              :disabled="!mergeReady"
              :loading="mergeBusy"
              @click="doMerge"
            >
              執行合併
            </UiButton>
          </template>
        </UiModal>

        <!-- 刪除客戶：列出會一併消失的東西、輸入名稱才能刪；尚有生效中授權則擋住 -->
        <UiModal :open="delOpen" title="刪除客戶" width="520px" @close="delOpen = false">
          <template v-if="delPreview">
            <p class="del-warn">
              即將永久刪除客戶 <strong>{{ delPreview.name }}</strong>，此操作無法復原。以下資料會一併刪除：
            </p>
            <ul class="del-list">
              <li>授權歷史 <strong>{{ delPreview.licenses }}</strong> 張（生效中 {{ delPreview.active_licenses }} 張）</li>
              <li>授權申請 <strong>{{ delPreview.requests }}</strong> 筆</li>
              <li>Portal 登入帳號 <strong>{{ delPreview.users }}</strong> 個</li>
              <li>主機連結 <strong>{{ delPreview.links }}</strong> 台、心跳紀錄 <strong>{{ delPreview.deployments }}</strong> 筆</li>
              <!-- 批93-B：加購是**另外收過錢**的東西，刪之前一定要讓操作者看見數量。 -->
              <li v-if="delPreview.addons">加購子憑證 <strong>{{ delPreview.addons }}</strong> 張</li>
              <!-- 批153：私人 plugin 的權利跟著客戶 CASCADE 刪掉（含已撤銷的歷史列）。 -->
              <li v-if="delPreview.plugin_grants">私人擴充的權利 <strong>{{ delPreview.plugin_grants }}</strong> 筆</li>
            </ul>
            <!-- 批141：平台客戶一律不可刪除（`deletable` 恆為 false），原因要講對，不是「還有授權」。 -->
            <p v-if="delPreview.platform" class="del-block">
              ⚠ 這是合作平台「{{ delPreview.platform.name }}」的組織（{{ delPreview.platform.external_ref }}）——平台客戶不可刪除。
            </p>
            <!-- 批153：plugin 發行者同樣恆為不可刪（`plugin_publishers.customer_id` 是 RESTRICT，後端 409）。 -->
            <p v-else-if="delPreview.plugin_publisher" class="del-block" data-testid="del-publisher">
              ⚠ 這個客戶是擴充發行者「{{ delPreview.plugin_publisher.display_name }}」<template
                v-if="delPreview.plugin_publisher.key_prefix"
              >（前綴 <code>{{ delPreview.plugin_publisher.key_prefix }}</code>）</template>——發行者身分（key 前綴、簽章公鑰、名下的擴充）綁在這個組織上，不能刪除。
            </p>
            <p v-else-if="!delPreview.deletable" class="del-block">
              ⚠ 尚有 {{ delPreview.active_licenses }} 張生效中授權——請先在主機列「停用」或到「授權」分頁撤銷，再回來刪除。
            </p>
            <label v-else class="field del-confirm">
              <span>請輸入客戶名稱「{{ delPreview.name }}」以確認</span>
              <input v-model="delName" :placeholder="delPreview.name" v-enter="doDelete" />
            </label>
          </template>
          <template #foot>
            <UiButton @click="delOpen = false">取消</UiButton>
            <UiButton
              variant="danger"
              :disabled="!delPreview?.deletable || delName.trim() !== delPreview?.name"
              :loading="delBusy"
              @click="doDelete"
            >
              永久刪除
            </UiButton>
          </template>
        </UiModal>

        <!-- 批93-B：加購子憑證。`:key` 讓每次開窗都是全新表單（ModulePicker 只在掛載時 initFrom）。 -->
        <AddonIssueModal
          v-if="addonHost"
          :key="`addon-${addonHost.instance_id}-${addonOpen}`"
          :open="addonOpen"
          :customer-id="selected.id"
          :instance-id="addonHost.instance_id"
          :license="addonHost.license"
          :product="addonHost.license?.product ?? null"
          :spec="store.productByKey(addonHost.license?.product)"
          :active-addons="addonHost.addons ?? []"
          :allow-internal="auth.isManagerOrAbove"
          @close="addonOpen = false"
          @issued="reloadHosts()"
        />
      </div>
      <div v-else class="card placeholder">← 選一個客戶以管理帳號與簽發授權</div>
    </div>
  </div>
</template>

<style scoped>
/* keyset 分頁的「載入更多」列（形狀與 LicensesView 一致） */
.more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  padding: var(--sp-3);
  border-top: 1px solid var(--border);
}

/* 批93-B：被加購提升的數字後面的小標（與 LicensesView 同一套） */
.ad-tag {
  margin-left: 4px;
  color: var(--brand-600, var(--fg-3));
  font-size: var(--fs-10, 10px);
  font-weight: 600;
  cursor: help;
}

/* 批93-B：主機列的加購 chips */
.addon-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}
.chip.addon {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: var(--fs-11);
  color: var(--brand-600, var(--fg-2));
  border: 1px solid var(--border-1);
}
.chip.addon.warn {
  color: var(--warning);
  border-color: var(--warning);
}
.chip.addon .x {
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.6;
  padding: 0 2px;
}
.chip.addon .x:hover {
  opacity: 1;
}

/* 批93-C：歸屬代理商（清單小標／詳情列／指派） */
.rs-tag {
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 999px;
  /* 批97：原本灰底灰字（--bg-hover／--fg-3）與網域小字幾乎同色、看不出是標籤（主人要求換色）。
     改品牌暖橘（logo 的 +1 色）：未填資料＝紅、邀 N＝青綠、代理商＝橘，三種標籤三種色。
     背景用半透明而非 --accent-100，深色主題下才不會是一塊亮奶油色。 */
  background: rgba(240, 168, 69, 0.18);
  color: var(--accent-600);
  font-size: var(--fs-10, 10px);
  /* 公司名長時整顆換行，不要在「測試／代理商 A」中間斷開 */
  white-space: nowrap;
}
/* 批141：合作平台的組織（與代理商標籤同形，改藍——青綠會跟選取列的底色撞在一起；
   已解除關聯的灰掉）。 */
.plat-tag {
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--st-transferred-bg);
  color: var(--st-transferred-fg);
  font-size: var(--fs-10, 10px);
  white-space: nowrap;
}
.plat-tag.gone {
  background: var(--bg-hover);
  color: var(--fg-3);
}
.plat-note {
  margin: 0 0 var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg-3);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.empty-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
}
.empty-opt {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: var(--sp-2) 0;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.assign-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin: var(--sp-2) 0 var(--sp-3);
  font-size: var(--fs-13);
}
.assign-val {
  color: var(--fg);
}
/* 政策提示（與 RequestsView 同一套） */
.policy {
  margin: var(--sp-3) 0 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: var(--r-sm);
  background: var(--bg);
  color: var(--fg-2);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.policy.ok {
  border-color: var(--brand-400);
  background: var(--brand-50);
  color: var(--brand-700, var(--fg));
}
.direct-chk {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
  font-size: var(--fs-13);
  color: var(--fg-2);
  line-height: var(--lh-base);
}
.direct-chk input {
  margin-top: 3px;
}
/* 破例（政策＝superadmin 卻硬簽）才染色——政策本來就允許的直發不該長得像警告。 */
.direct-chk.glass {
  border-left: 3px solid var(--danger, var(--fg-2));
  padding-left: var(--sp-2);
}
/* 手機／平板（批171）：勾選標籤與長說明擠在同一列時「免審核發（不經申請流程）」被壓成三行 → 說明換到下一列。 */
@media (--compact) {
  .direct-chk {
    flex-wrap: wrap;
    row-gap: 2px;
  }
  .direct-chk .subtle {
    flex: 1 1 100%;
    padding-left: 22px;
  }
}
/* 批113：按鈕上方那句「草稿也會帶啟用／到期時刻」。 */
.timing-note {
  margin-top: var(--sp-2);
}
/* 合併對話框 */
.merge-list {
  list-style: none;
  margin: var(--sp-2) 0;
  padding: 0;
  max-height: 200px;
  overflow: auto;
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
}
.merge-list li {
  padding: 6px 10px;
  cursor: pointer;
  font-size: var(--fs-13);
  color: var(--fg-2);
}
.merge-list li:hover {
  background: var(--bg-hover);
}
.merge-list li.sel {
  background: var(--brand-50);
  color: var(--brand-700);
  font-weight: 600;
}
.merge-done {
  margin: 0 0 var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg);
}
.sub-title {
  margin: var(--sp-3) 0 var(--sp-1);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.chk {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  margin: var(--sp-2) 0;
  font-size: var(--fs-13);
  color: var(--fg-2);
  line-height: var(--lh-base);
}
.chk.warn-chk {
  color: var(--warning);
}
/* ★ 不可叫 `.warn`（scoped 樣式會套到 UiButton variant="warn" 的根節點）。 */
.warn-text {
  margin: var(--sp-2) 0 0;
  color: var(--warning);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}

/* 批65：套裝選擇列 */
.prod-field {
  max-width: 320px;
  margin-bottom: var(--sp-2);
}
.plan-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}
.plan-label {
  font-size: var(--fs-13);
  color: var(--fg-3);
}
.plan-chip {
  padding: 4px 12px;
  border: 1px solid var(--border-2);
  border-radius: 999px;
  background: transparent;
  color: var(--fg-2);
  font-size: var(--fs-13);
  cursor: pointer;
}
.plan-chip.on {
  border-color: var(--brand-500);
  background: var(--brand-500);
  color: #fff;
}
/* 批155：內部套裝——非營運主管看得到現行證的那一顆，但按不下去。 */
.plan-chip:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.int-mark {
  margin-left: 4px;
  font-size: var(--fs-11);
  opacity: 0.8;
}
.quota-title {
  margin: var(--sp-3) 0 var(--sp-1);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.page-head {
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: var(--sp-4);
  align-items: start;
}
.create-row {
  display: flex;
  gap: var(--sp-2);
}
.create-row input {
  flex: 1;
  min-width: 0;
}
.domain {
  width: 100%;
  margin: var(--sp-2) 0 0;
}
.domain-hint {
  margin: var(--sp-1) 0 0;
  font-size: var(--fs-11);
  color: var(--fg-4);
  line-height: var(--lh-base);
}
.kind-sel {
  width: 100%;
  margin-top: var(--sp-2);
}
.unset-tag,
.ref-tag {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  white-space: nowrap;
  font-size: var(--fs-11);
}
.unset-tag {
  background: rgba(209, 67, 67, 0.12);
  color: var(--danger);
}
.ref-tag {
  background: var(--brand-50);
  color: var(--brand-700);
}
/* 批97 Bug 7：`.pf`（基本資料的 dt/dd 表）與 `.needs` 已搬進共用元件
   `components/CustomerProfileCard.vue`——那張表兩個 realm 都要用，樣式跟著模板走。 */
.ref-block {
  display: grid;
  gap: 6px;
  padding: var(--sp-3);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
}
.ref-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  font-size: var(--fs-13);
}
.ref-k {
  flex: 0 0 84px;
  color: var(--fg-3);
  font-size: var(--fs-12);
}
.lnk-static {
  color: var(--brand-600);
  font-weight: 600;
}
.mono {
  font-family: ui-monospace, monospace;
  letter-spacing: 1px;
}
.mp {
  margin: var(--sp-3) 0;
}
.list {
  list-style: none;
  margin: var(--sp-2) 0 0;
  padding: 0;
}
.list li {
  padding: 8px 10px;
  border-radius: var(--r-sm);
  cursor: pointer;
  font-size: var(--fs-13);
}
.list li:hover {
  background: var(--bg-hover);
}
.list li.sel {
  background: var(--brand-50);
  color: var(--brand-700);
  font-weight: 600;
}
.dom {
  color: var(--fg-4);
  font-size: var(--fs-11);
}
/* 客戶名稱列：名稱＋網域＋✎；編輯態換成兩個輸入框 */
.cust-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
}
.cust-name {
  margin: 0;
  font-size: var(--fs-15);
}
.cust-in {
  flex: 1;
  min-width: 160px;
}
.del-btn {
  margin-left: auto;
}
.del-warn {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-13);
  color: var(--fg-2);
  line-height: var(--lh-base);
}
.del-list {
  margin: 0 0 var(--sp-3);
  padding-left: 1.2em;
  font-size: var(--fs-13);
  color: var(--fg-2);
  line-height: 1.8;
}
.del-block {
  margin: 0;
  font-size: var(--fs-13);
  color: var(--danger);
  line-height: var(--lh-base);
}
.del-confirm span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
/* 區段標題列（標題＋右側按鈕）；section-title 的段落間距由這層負責 */
.sec-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  margin: var(--sp-5) 0 var(--sp-3);
}
.sec-head .section-title {
  margin: 0;
}
.chips {
  list-style: none;
  margin: var(--sp-2) 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.chip .x,
.x {
  border: none;
  background: none;
  color: var(--fg-4);
  cursor: pointer;
  font-size: 13px;
  padding: 0 2px;
}
.x:hover {
  color: var(--brand-600);
}
/* 批114：配額摘要欄。給一個上限寬度，`QuotaSummary` 的省略號才有作用——1366px 的
   筆電上，這一欄一折行就會把整張表推爆（配額鍵的數量是產品自己決定的）。 */
.quota {
  max-width: 230px;
  white-space: nowrap;
}
/* 批114（#5540）：離線授權列的標記（沒有連結主機＝氣隙或請求檔核發） */
.off-tag {
  display: inline-block;
  padding: 0 6px;
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  color: var(--fg-3);
  font-size: var(--fs-11);
  cursor: help;
}
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
  margin: var(--sp-3) 0;
}
.field span {
  font-size: var(--fs-12);
  color: var(--fg-2);
}
.placeholder {
  display: grid;
  place-items: center;
  color: var(--fg-4);
  min-height: 200px;
}
/* 手機／平板（批171）：右欄的 `1fr` 是 `minmax(auto, 1fr)`，會被卡片內表格的最小寬度撐開、整欄超出畫面
   → 釘成 `minmax(0, 1fr)`，表格改在卡片內橫捲。 */
@media (--compact) {
  .grid {
    grid-template-columns: 320px minmax(0, 1fr);
  }
  .grid > .card {
    min-width: 0;
  }
}
@media (max-width: 900px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .form {
    grid-template-columns: 1fr;
  }
}
</style>
